// features/findings/substrate-symmetric-refugia.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — Substrate-Symmetric Refugia (v1)
// Form 4 — Pattern across cases (cross-substrate). Pre-registered 2026-05-08.
//
// The sixth popdec finding. First substrate-symmetric extension: tests
// whether the seven-condition framework introduced in Israel-Haredi
// Floor (Form 1) and validated cross-case in Refugia Template (Form 4)
// predicts population recovery and collapse across non-human cases.
//
// Ten well-documented cases: 3 sustained recovery (mountain gorilla,
// Iberian lynx, gray wolf Yellowstone), 2 transitional (Atlantic cod
// Iceland, whooping crane), 3 sustained collapse (Atlantic cod NW
// Atlantic, North Atlantic right whale, vaquita), 2 framework-boundary
// cases (California condor intervention substitution, caribou boreal
// external-suppression). Atlantic cod NW Atlantic is the load-bearing
// failure analog (analog of kibbutz in human refugia template).
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
} from './components-shared';
import {
  CoOccurrenceMatrix,
  CountryScatter,
  DivergenceTimeline,
  PredictionsBlock,
  MagnitudeBars,
} from './charts';

// ─── Chart data ─────────────────────────────────────────────────────

// Co-occurrence matrix — eco cases × eco-conditions.
const ECO_CASES = [
  { id: 'gorilla',     shortLabel: 'Mountain gorilla',     outcome: 'sustained' },
  { id: 'lynx',        shortLabel: 'Iberian lynx',         outcome: 'sustained' },
  { id: 'wolf',        shortLabel: 'Gray wolf YS',         outcome: 'sustained' },
  { id: 'cod-iceland', shortLabel: 'Atlantic cod Iceland', outcome: 'transitional' },
  { id: 'whooping',    shortLabel: 'Whooping crane',       outcome: 'transitional' },
  { id: 'cod-nw',      shortLabel: 'Atlantic cod NW',      outcome: 'failed' },
  { id: 'right-whale', shortLabel: 'NA right whale',       outcome: 'failed' },
  { id: 'vaquita',     shortLabel: 'Vaquita',              outcome: 'failed' },
  { id: 'condor',      shortLabel: 'CA condor (interv.)',  outcome: 'partial' },
  { id: 'caribou',     shortLabel: 'Boreal caribou',       outcome: 'failed' },
];

const ECO_CONDITIONS = [
  {
    id: 'e1',
    label: 'E1 — Aggregation / colony density',
    values: {
      'gorilla':     { state: 'present' },
      'lynx':        { state: 'partial' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'present' },
      'whooping':    { state: 'absent' },
      'cod-nw':      { state: 'absent' },
      'right-whale': { state: 'absent' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'partial' },
      'caribou':     { state: 'partial' },
    },
  },
  {
    id: 'e2',
    label: 'E2 — Habitat-network connectivity',
    values: {
      'gorilla':     { state: 'partial' },
      'lynx':        { state: 'present' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'partial' },
      'whooping':    { state: 'partial' },
      'cod-nw':      { state: 'absent' },
      'right-whale': { state: 'partial' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'partial' },
      'caribou':     { state: 'absent' },
    },
  },
  {
    id: 'e3',
    label: 'E3 — Reproduction-permitting hierarchy',
    values: {
      'gorilla':     { state: 'partial' },
      'lynx':        { state: 'present' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'present' },
      'whooping':    { state: 'present' },
      'cod-nw':      { state: 'partial' },
      'right-whale': { state: 'present' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'present' },
      'caribou':     { state: 'present' },
    },
  },
  {
    id: 'e4',
    label: 'E4 — Cooperative breeding / alloparenting',
    values: {
      'gorilla':     { state: 'present' },
      'lynx':        { state: 'partial' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'absent' },
      'whooping':    { state: 'partial' },
      'cod-nw':      { state: 'absent' },
      'right-whale': { state: 'partial' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'partial' },
      'caribou':     { state: 'present' },
    },
  },
  {
    id: 'e5',
    label: 'E5 — Behavioral / cultural transmission',
    values: {
      'gorilla':     { state: 'present' },
      'lynx':        { state: 'partial' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'partial' },
      'whooping':    { state: 'present' },
      'cod-nw':      { state: 'absent' },
      'right-whale': { state: 'absent' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'partial' },
      'caribou':     { state: 'present' },
    },
  },
  {
    id: 'e6',
    label: 'E6 — Mate-finding / spawning infrastructure',
    values: {
      'gorilla':     { state: 'present' },
      'lynx':        { state: 'partial' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'present' },
      'whooping':    { state: 'partial' },
      'cod-nw':      { state: 'absent' },
      'right-whale': { state: 'absent' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'partial' },
      'caribou':     { state: 'present' },
    },
  },
  {
    id: 'e7',
    label: 'E7 — Site fidelity / philopatry',
    values: {
      'gorilla':     { state: 'present' },
      'lynx':        { state: 'present' },
      'wolf':        { state: 'present' },
      'cod-iceland': { state: 'present' },
      'whooping':    { state: 'present' },
      'cod-nw':      { state: 'partial' },
      'right-whale': { state: 'partial' },
      'vaquita':     { state: 'absent' },
      'condor':      { state: 'present' },
      'caribou':     { state: 'present' },
    },
  },
];

// Cross-case scatter — eco condition count (x) × population trajectory (y).
// y axis: population growth rate (% per year) over the most recent
// 10-year window. Sustained-recovery cases at +5-15%/yr; collapse at
// -2 to -7%/yr; transitional at 0 to +5%/yr. Boundary cases marked.
const ECO_SCATTER = [
  { label: 'Mountain gorilla',     x: 6.0, y: 3.5,  tone: 'sage' },
  { label: 'Gray wolf YS',         x: 6.5, y: 8.0,  tone: 'sage' },
  { label: 'Iberian lynx',         x: 5.0, y: 16.0, tone: 'sage' },
  { label: 'Atlantic cod Iceland', x: 4.0, y: 1.5,  tone: 'forest-moss' },
  { label: 'Whooping crane',       x: 3.5, y: 4.0,  tone: 'forest-moss' },
  { label: 'CA condor (interv.)',  x: 3.5, y: 5.5,  tone: 'brass', outlier: true, labelDx: 8, labelDy: 12 },
  { label: 'Boreal caribou',       x: 5.0, y: -4.0, tone: 'oxblood', outlier: true, labelDx: -100, labelDy: 0 },
  { label: 'NA right whale',       x: 1.5, y: -2.5, tone: 'oxblood' },
  { label: 'Atlantic cod NW',      x: 0.5, y: 0.0,  tone: 'oxblood' },
  { label: 'Vaquita',              x: 0.0, y: -45.0, tone: 'oxblood' },
];

// Trajectory series — recovery + collapse + boundary cases.
// X: year. Y: population index (% of peak or current absolute).
// All series normalized to be readable on one chart.
const TRAJECTORY_SERIES = [
  {
    id: 'cod-nw',
    label: 'Atlantic cod NW (% of 1960s peak)',
    color: 'oxblood',
    points: [
      { x: 1965, y: 100 },
      { x: 1975, y: 80 },
      { x: 1985, y: 50 },
      { x: 1990, y: 25 },
      { x: 1992, y: 1.4 },
      { x: 2000, y: 3 },
      { x: 2010, y: 8 },
      { x: 2020, y: 15 },
      { x: 2024, y: 18 },
    ],
  },
  {
    id: 'gorilla',
    label: 'Mountain gorilla (count, scaled)',
    color: 'sage',
    points: [
      { x: 1980, y: 25 },
      { x: 1989, y: 48 },
      { x: 2000, y: 72 },
      { x: 2010, y: 80 },
      { x: 2018, y: 106 },
      { x: 2024, y: 110 },
    ],
  },
  {
    id: 'lynx',
    label: 'Iberian lynx (count/100)',
    color: 'forest-moss',
    points: [
      { x: 2002, y: 1 },
      { x: 2010, y: 2.5 },
      { x: 2015, y: 4 },
      { x: 2020, y: 11 },
      { x: 2024, y: 17 },
    ],
  },
  {
    id: 'right-whale',
    label: 'NA right whale (count/10)',
    color: 'cognac',
    points: [
      { x: 1990, y: 27 },
      { x: 2000, y: 35 },
      { x: 2010, y: 47 },
      { x: 2017, y: 41 },
      { x: 2020, y: 36 },
      { x: 2024, y: 34 },
    ],
  },
];

// Per-case condition count and population status.
const ECO_CONDITION_BARS = [
  { label: 'Gray wolf Yellowstone (6.5/7) — recovery +8%/yr',         value: 6.5, tone: 'sage' },
  { label: 'Mountain gorilla (6.0/7) — recovery +3.5%/yr',             value: 6.0, tone: 'sage' },
  { label: 'Iberian lynx (5.0/7) — recovery +16%/yr',                  value: 5.0, tone: 'sage' },
  { label: 'Boreal caribou (5.0/7, external-suppression) — DECLINE',  value: 5.0, tone: 'oxblood' },
  { label: 'Atlantic cod Iceland (4.0/7) — sustained transitional',    value: 4.0, tone: 'forest-moss' },
  { label: 'Whooping crane (3.5/7, interv.-supported) — slow recovery', value: 3.5, tone: 'brass' },
  { label: 'CA condor (3.5/7, interv.-restored) — managed recovery',   value: 3.5, tone: 'brass' },
  { label: 'NA right whale (1.5/7) — declining',                       value: 1.5, tone: 'oxblood' },
  { label: 'Atlantic cod NW (0.5/7, post-collapse) — no recovery',     value: 0.5, tone: 'oxblood' },
  { label: 'Vaquita (0.0/7) — functional extinction',                  value: 0.0, tone: 'oxblood' },
];

// ─── Predictions ────────────────────────────────────────────────────

export const ECO_PREDICTIONS = [
  {
    label: 'P1 — Mountain gorilla Virunga ≥1200 by 2030',
    central: 1300,
    band: [1200, 1400],
    falsifierBand: [1100, 1500],
    resolutionDate: '2030 IUCN red-list reassessment + GVTC census',
    status: 'open',
    note: 'Sustained-recovery case at 6/7 conditions. Framework predicts continued growth while conditions hold. Falsified if 2030 population <1100 (which would imply condition-decay we have not yet detected).',
  },
  {
    label: 'P2 — Atlantic cod NW Atlantic stays <50% recovery target through 2030',
    central: 25,
    band: [15, 40],
    falsifierBand: [10, 50],
    resolutionDate: '2030 DFO Canada stock assessment',
    status: 'open',
    note: 'Failure analog. 30+ years moratorium without recovery is the framework prediction. Falsified if SSB ≥50% of recovery reference point by 2030 — which would imply conditions are reforming despite below-Allee density.',
  },
  {
    label: 'P3 — Iberian lynx >2500 by 2030',
    central: 2800,
    band: [2500, 3200],
    falsifierBand: [2000, 3500],
    resolutionDate: '2030 IUCN red-list census + Spain/Portugal national surveys',
    status: 'open',
    note: 'Sustained-recovery case under continued connectivity expansion. Population grew 94→1668 across 2002-2024 at average 16% annual; framework predicts continued growth as habitat-network strengthens.',
  },
  {
    label: 'P4 — Vaquita reaches functional extinction by 2030',
    central: 0,
    band: [0, 5],
    falsifierBand: [0, 10],
    resolutionDate: '2030 IUCN + Mexican CIRVA reports',
    status: 'open',
    note: '0/7 conditions; <10 individuals (2024). Framework predicts functional extinction. Falsified if 2030 population ≥10 (which would imply some condition recovery we have not detected).',
  },
  {
    label: 'P5 — NA right whale <320 by 2030',
    central: 300,
    band: [280, 320],
    falsifierBand: [260, 360],
    resolutionDate: '2030 NOAA + NARWC photo-ID census',
    status: 'open',
    note: '1-2/7 conditions; declining 2-3% annually. Framework predicts continued decline absent restoration of behavioral-cultural transmission and aggregation density. Ship-strike reduction alone (an external-suppression removal) is not predicted to reverse the trend without condition restoration.',
  },
  {
    label: 'P6 — Boreal caribou: ≥60% of monitored herds decline despite intact ≥4/7 conditions through 2030',
    central: 75,
    band: [60, 85],
    falsifierBand: [40, 95],
    resolutionDate: '2030 ECCC range assessment',
    status: 'open',
    note: 'Framework-boundary test. If herds with intact structural conditions still decline, the external-suppression-dominance claim is supported. If herds recover with intact conditions despite climate change, the framework over-claims and needs revision.',
  },
  {
    label: 'P7 — California condor: ≥30% wild-wild reproduction by 2030',
    central: 35,
    band: [25, 45],
    falsifierBand: [15, 60],
    resolutionDate: '2030 USFWS California Condor Recovery Program',
    status: 'open',
    note: 'Intervention-substitution test. Wild-wild reproduction (both parents wild-fledged) is the indicator that structural conditions have reformed autonomously. <15% would suggest intervention dependence remains; ≥30% would suggest the framework\'s "autonomous reformation requires above-threshold conditions" claim holds with intervention as an effective bootstrap.',
  },
  {
    label: 'P8 — Anti-prediction: zero marine teleost stocks recover above 50% B(MSY) without conditions reforming, by 2035',
    central: 0,
    band: [0, 0],
    falsifierBand: [0, 1],
    resolutionDate: '2035 FAO + ICES + DFO + NOAA stock assessments review',
    status: 'open',
    note: 'Single-falsifier-suffices design. Tests whether the Allee-threshold claim generalizes across marine teleost stocks. If any stock that collapsed below 0-1/7 recovers to 50% B(MSY) within 30 years without active aggregation-restoration intervention, the framework\'s autonomous-reformation impossibility claim is wounded.',
  },
];

// ─── Finding metadata ───────────────────────────────────────────────

export const FINDING_META = {
  slug: 'substrate-symmetric-refugia',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/substrate-symmetric-refugia/v1',
  title: 'Substrate-Symmetric Refugia: Whether the Seven-Condition Framework Predicts Population Recovery Across Non-Human Cases',
  shortTitle: 'Substrate-Symmetric Refugia',
  pageTitle: 'Substrate-Symmetric Refugia — Lab Finding · Shift',
  description:
    'Cross-substrate test of the seven-condition framework. Ten well-documented ecological cases: 3 sustained recovery (mountain gorilla, Iberian lynx, gray wolf Yellowstone), 2 transitional, 3 sustained collapse, 2 framework-boundary cases (CA condor intervention substitution, boreal caribou external-suppression). Atlantic cod NW Atlantic is the load-bearing failure analog. 8 pre-registered predictions resolving 2030-2035.',
  form: 4,
  formName: 'Pattern across cases (cross-substrate)',
  reflexivity: 'MEDIUM',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 10,
  preregisteredAt: '2026-05-08',
  visibility: 'public-peek',
};

// ─── Article ────────────────────────────────────────────────────────

export default function SubstrateSymmetricRefugiaFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="§ Lab finding · popdec/2026/substrate-symmetric-refugia/v1"
        title={FINDING_META.title}
        sub="The sixth popdec finding. First substrate-symmetric extension."
        subItalic="Five prior findings establish the seven-condition framework on human populations. The Continuum project's distinguishing thesis is substrate-symmetry: the same structural mechanism should operate, at appropriate analog, in non-human population biology. Ten ecological cases test this — and two boundary cases (caribou, condor) document where the framework needs supplementation."
        meta={FINDING_META}
      />

      <section className="finding-lede">
        <p>
          The seven-condition framework, operationalized for non-human substrates, predicts population recovery and collapse outcomes across documented wildlife cases within the same super-linear pattern observed in human cases. Sustained recovery (mountain gorilla, Iberian lynx, gray wolf Yellowstone) requires 5+ structural conditions; sustained collapse (Atlantic cod NW Atlantic, North Atlantic right whale, vaquita) sits at 0-2 conditions. The Atlantic cod NW Atlantic case provides the load-bearing failure analog: documented ~5/7 conditions before 1980s, conditions decayed through overfishing, Allee threshold breached 1992, no recovery despite 30+ years moratorium. Two boundary cases (California condor, caribou boreal) document where the framework needs supplementation: external-suppression dominance and intervention-substitution. Below: the case matrix, the cross-substrate scatter, the cod NW Atlantic trajectory, eight pre-registered predictions, and the falsifier.
        </p>
      </section>

      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          <strong>The framework generalizes across substrates with two boundary supplements.</strong> Eco-condition count predicts wildlife recovery vs collapse outcomes. The cod NW Atlantic case confirms directionality (lift while conditions held, collapse when they decayed, no recovery despite intervention removal). The framework needs supplementing for cases where external suppression dominates (caribou under climate change) or intervention substitutes for autonomous condition reformation (California condor). Bald eagle is the counter-test: recovery via DDT-removal alone proves the framework predicts the structural-conditions axis specifically, not the external-suppression axis.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">cross-substrate validated</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=10 cases</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-08</FindingPill>
          <FindingPill tone="neutral">Reflexivity: MEDIUM</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Mountain gorilla, Iberian lynx, Gray wolf Yellowstone, Atlantic cod Iceland, whooping crane, Atlantic cod NW Atlantic (failure analog), NA right whale, vaquita, California condor (intervention-substitution boundary), boreal caribou (external-suppression boundary). Sources: IUCN red-list, NOAA, DFO Canada, ECCC, USFWS, Greater Virunga Transboundary Collaboration, NARWC photo-ID database.
        </div>
      </section>

      {/* ── §1 The substrate-symmetry claim ── */}
      <FindingSection
        eyebrow="§1 The substrate-symmetry claim"
        title="From human cohorts to wildlife populations."
      >
        <p className="finding-prose">
          The Continuum project's central distinguishing thesis is that the same mechanism produces population recovery (or fails to) across substrates. The five prior popdec findings establish the human-substrate version. The same structural conditions, appropriately translated, should operate in non-human population biology — schooling fish, large mammals, colonial seabirds, social cetaceans — wherever cooperative reproduction, density-dependent dynamics, and behavioral-cultural transmission can shape population-level outcomes.
        </p>
        <p className="finding-prose">
          The translation isn't trivial. Religion-as-community-density-generator has no eco-substrate analog; what does is <em>spawning aggregation density</em>, <em>colony density</em>, <em>pack/troop density</em>. Multi-generational household economics translates to <em>habitat-network connectivity</em> — the structural condition that allows source-sink dynamics and rescue effects across patches. Status-decoupled reproduction translates to <em>territory-inheritance / queue-based hierarchies</em> that don't suppress lower-rank breeding. And so on.
        </p>

        <FindingSubhead>The seven conditions, eco-substrate operationalization</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>E1 — Aggregation / colony density</strong> (analog of C1: religious-community density)</li>
          <li><strong>E2 — Habitat-network connectivity</strong> (analog of C2: multi-gen household economics)</li>
          <li><strong>E3 — Reproduction-permitting hierarchy</strong> (analog of C3: status decoupled from reproduction)</li>
          <li><strong>E4 — Cooperative breeding / alloparenting</strong> (analog of C4: kinship-network childcare)</li>
          <li><strong>E5 — Behavioral / cultural transmission</strong> (analog of C5: identity-narrative coherence)</li>
          <li><strong>E6 — Mate-finding / spawning infrastructure</strong> (analog of C6: marriage-formation infrastructure)</li>
          <li><strong>E7 — Site fidelity / philopatry</strong> (analog of C7: exit cost)</li>
        </ul>

        <p className="finding-prose">
          The cross-substrate claim:
        </p>
        <blockquote className="finding-quote">
          The eco-condition count predicts the population-recovery outcome across documented non-human cases, with the same super-linear relationship observed in human cases. The Atlantic cod NW Atlantic failure analog confirms directionality.
        </blockquote>
      </FindingSection>

      {/* ── §2 The case matrix ── */}
      <FindingSection
        eyebrow="§2 The case matrix"
        title="Ten cases × seven conditions."
      >
        <p className="finding-prose">
          The matrix below documents which conditions are present (sage), partial (brass), or absent (oxblood) in each case. The pattern: <strong>condition density predicts population trajectory; the cases segregate cleanly into sustained-recovery, transitional, and sustained-collapse bands. Two boundary cases (CA condor, boreal caribou) sit outside the central pattern in instructive ways.</strong>
        </p>

        <CoOccurrenceMatrix
          title="Ten cases × seven structural conditions [eco-substrate cell calls are mechanism-dossier judgments; translation confidence varies — high for E1/E2/E7, medium for E4, lower for E3/E5/E6 per §7 caveats]. Sustained-recovery cases (sage outcome) cluster at 5+/7 conditions; sustained-collapse cases (oxblood outcome) at 0-2/7. Boundary cases: California condor (intervention-substitution at 3.5/7), boreal caribou (external-suppression-dominance despite 5/7)."
          cases={ECO_CASES}
          signals={ECO_CONDITIONS}
        />

        <p className="finding-prose">
          <strong>Eco-substrate cell calls are mechanism-dossier judgments, not measurements.</strong> The translation confidence varies sharply across conditions (already disclosed in §7 caveats). E1 (aggregation density), E2 (habitat-network connectivity), and E7 (site fidelity) translate cleanly from human conditions and are well-established in population ecology — these cell calls are the most defensible. E4 (cooperative breeding) is medium confidence — it applies cleanly to species with allomothering but does not apply to broadcast spawners (cod, tuna), so those cells are coded "absent" by inapplicability, not by absence of evidence. <strong>E3 (reproduction-permitting hierarchy), E5 (cultural transmission), and E6 (mate-finding infrastructure) are the lowest-confidence translations</strong> — they vary enormously across taxa and the present/partial/absent calls compress real heterogeneity. A critic could legitimately push 4-5 specific cell calls (gray wolf E3, mountain gorilla E3, cod-Iceland E5, right-whale E5) by ±1 step. The pattern (count → trajectory) is robust to small cell-call shifts, but the boundary cases (condor, caribou) demonstrate that count alone is insufficient.
        </p>

        <FindingSubhead>The cross-substrate scatter</FindingSubhead>
        <p className="finding-prose">
          Plotting condition count (x) against population growth rate (y, % per year over the most recent 10-year window) reveals the pattern. The boundary cases (caribou, condor) sit conspicuously off the central trend.
        </p>

        <CountryScatter
          title="Condition count × population growth rate (% per year, 10-year window). Central trend: super-linear above 5/7. Caribou outlier (oxblood) sits at 5/7 with negative growth — external suppression. Condor outlier (brass) sits at 3.5/7 with positive growth — intervention substitution."
          points={ECO_SCATTER}
          xLabel="Eco-condition count (of 7)"
          yLabel="Population growth rate (% per year, 10-yr window)"
          xMin={-0.5}
          xMax={7.5}
          yMin={-50}
          yMax={20}
        />

        <FindingSubhead>Per-case condition count and trajectory</FindingSubhead>
        <p className="finding-prose">
          Sorted by condition count. Note the boundary cases: caribou at 5/7 with declining trajectory (oxblood, contradicts central trend); condor at 3.5/7 with managed recovery (brass, intervention substitutes for autonomous reformation).
        </p>

        <MagnitudeBars
          title="Per-case eco-condition count next to population status. Sage = sustained-recovery; forest-moss = transitional; brass = intervention-supported; oxblood = collapse, declining, or framework-boundary external-suppression."
          items={ECO_CONDITION_BARS}
          valueLabel="condition count"
        />
      </FindingSection>

      {/* ── §3 The mechanism — why super-linearity should hold ── */}
      <FindingSection
        eyebrow="§3 The mechanism"
        title="Why super-linearity transfers across substrates."
      >
        <p className="finding-prose">
          The conditions reinforce each other in human cases (per Refugia Template §3); the same compounding pairs operate at eco-substrate analogs:
        </p>
        <ul className="finding-bullets">
          <li><strong>E1 (aggregation density) + E6 (mate-finding infrastructure).</strong> Density makes mate-finding viable; mate-finding infrastructure (chemical cues, lekking sites, synchronized timing) requires density to operate. Below threshold, both fail simultaneously. Atlantic cod NW Atlantic documents this: spawning aggregation density collapsed → synchronized spawning timing broke down → fertilization rates collapsed.</li>
          <li><strong>E2 (habitat-network) + E7 (site fidelity).</strong> Connected habitat patches with site-faithful sub-populations produce source-sink dynamics that buffer against local collapses. Iberian lynx documents this: re-establishing connectivity allowed source patches to rescue declining sub-populations.</li>
          <li><strong>E4 (cooperative breeding) + E5 (cultural transmission).</strong> Cooperative-breeder species (gorilla, wolf, elephant, cetaceans) transmit foraging routes, predator-recognition, and social structure across generations through cultural learning. Loss of older individuals can disrupt both cooperative care AND cultural transmission simultaneously. NA right whale documents this — older females navigate to seasonal feeding grounds; their loss disrupts the next generation's foraging knowledge.</li>
          <li><strong>E3 (reproduction-permitting hierarchy) is the most variable.</strong> In broadcast spawners (cod), hierarchy is largely irrelevant. In cooperative breeders (wolf, gorilla), territory inheritance and queue dynamics permit lower-rank reproduction. In eusocial species (naked mole rat, social bees), hierarchy strictly limits reproduction — a different dynamic the framework would need separate operationalization for.</li>
          <li><strong>E7 (site fidelity) stabilizes everything else.</strong> Without site fidelity, even intact aggregation density and habitat networks produce drift. The Allee threshold is reached faster in low-philopatry species because local declines aren't compensated by faithful re-aggregation.</li>
        </ul>
        <p className="finding-prose">
          This mutual reinforcement produces the super-linear shape across substrates. The relationship isn't perfectly identical to the human-case super-linearity — eco-substrate conditions are operationalized at different effective grains — but the pattern holds: 5+ conditions sustain recovery; below 3 conditions, recovery is rare or impossible without intensive intervention.
        </p>
      </FindingSection>

      {/* ── §4 The failure analog — Atlantic cod NW Atlantic ── */}
      <FindingSection
        eyebrow="§4 The failure analog"
        title="Atlantic cod NW Atlantic: the eco-substrate kibbutz."
      >
        <p className="finding-prose">
          Atlantic cod NW Atlantic is the load-bearing failure analog for the eco substrate, parallel to kibbutz in the human substrate. It documents the framework's prediction in two directions: lift while conditions held, collapse when they decayed, no recovery despite intervention.
        </p>

        <FindingSubhead>Conditions present pre-1980s</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>E1 — Spawning aggregation density: HIGH.</strong> Cod aggregated in dense seasonal spawning concentrations on Grand Banks shelf. This was the demographic lever — fertilization success, predator dilution, mate-finding all depend on density above an Allee threshold.</li>
          <li><strong>E7 — Site fidelity: HIGH.</strong> Cod returned to natal spawning grounds with strong philopatry. Population sub-structure was preserved across the stock complex.</li>
          <li><strong>E2 — Habitat-network connectivity: HIGH.</strong> Multiple sub-populations connected through larval drift and adult migration.</li>
          <li><strong>E6 — Mate-finding infrastructure: HIGH.</strong> Synchronized spawning timing, dense aggregations, physical proximity all permitted high fertilization rates.</li>
        </ul>

        <p className="finding-prose">
          At ~5/7 conditions, the stock sustained 1.6 million tonnes biomass. The Grand Banks cod fishery was the largest in the world for 400 years.
        </p>

        <FindingSubhead>Conditions destroyed through overfishing</FindingSubhead>
        <p className="finding-prose">
          Industrial fishing concentrated on the spawning aggregations (because they were predictable and dense). This selectively destroyed E1 — spawning aggregation density collapsed below the Allee threshold by 1990. Synchronized spawning broke down (E6 collapsed). Site fidelity weakened as sub-populations fragmented (E7 weakened). The structural conditions decayed in stages.
        </p>
        <p className="finding-prose">
          By 1992, conditions had collapsed to ~1/7. The stock crashed from 1.6 million tonnes to 22,000 tonnes — a 99% reduction.
        </p>

        <FindingSubhead>No recovery despite 30+ years moratorium</FindingSubhead>
        <p className="finding-prose">
          A complete fishing moratorium in 1992 was expected to produce recovery within 5-10 years. Instead, 30+ years later, the stock remains below 25% of recovery target. <strong>The framework explains why: removing the suppression (overfishing) does not restore the conditions.</strong> Spawning aggregation density (E1) requires sufficient biomass to reform spontaneously, but biomass cannot grow without aggregation density (the Allee dependency). Site fidelity (E7) is preserved in older fish but the stock's age structure was destroyed by selective fishing on large spawners. Mate-finding infrastructure (E6) requires synchronized spawning at density, which the remaining stock cannot achieve.
        </p>

        <DivergenceTimeline
          title="Trajectories that confirm the framework's directionality. Atlantic cod NW (oxblood) collapsed from 100% of 1960s peak to 1.4% by 1992; 30+ years moratorium has stalled recovery at ~18%. Mountain gorilla (sage) and Iberian lynx (forest-moss) show sustained recovery as conditions strengthen. NA right whale (cognac) recovered partially through 2010 then began declining again as condition-decay (cultural-transmission disruption) accelerated."
          series={TRAJECTORY_SERIES}
          xLabel="Year"
          yLabel="Population index"
          yMin={0}
          yMax={120}
        />

        <FindingSubhead>Why this is the load-bearing failure analog</FindingSubhead>
        <p className="finding-prose">
          Just as kibbutz documented the framework's directionality in human cases (lift while ~4/7 conditions held; collapse when conditions decayed), Atlantic cod NW Atlantic documents the framework's directionality in eco cases:
        </p>
        <ol className="finding-numbered-list">
          <li><strong>Lift while conditions held:</strong> ~5/7 conditions sustained the largest cod fishery in the world for 400 years.</li>
          <li><strong>Collapse when conditions decayed:</strong> overfishing selectively destroyed E1 spawning aggregation density, collapsing the structural stack.</li>
          <li><strong>No recovery despite intervention removal:</strong> 30+ years moratorium has not restored the conditions because the conditions require above-threshold density to reform.</li>
        </ol>
        <p className="finding-prose">
          If the framework were wrong, removing the suppression should have produced recovery (as occurred with bald eagle following DDT removal — see §6). The fact that 30+ years moratorium has not restored cod confirms that the structural conditions — not just the absence of overfishing — are the operative variable.
        </p>
      </FindingSection>

      {/* ── §5 The boundary cases ── */}
      <FindingSection
        eyebrow="§5 The boundary cases"
        title="Where the framework needs supplementation."
      >
        <p className="finding-prose">
          Two cases document the framework's boundaries in instructive ways: California condor (intervention substitution) and boreal caribou (external-suppression dominance).
        </p>

        <FindingSubhead>California condor — intervention substitution</FindingSubhead>
        <p className="finding-prose">
          22 individuals in 1987, all captured for captive-breeding. Population reached 500+ by 2024 through release-and-management. The structural conditions (aggregation density, behavioral transmission of foraging routes, philopatry to release sites) were artificially restored through intensive intervention.
        </p>
        <p className="finding-prose">
          The framework's prediction at the structural-conditions-only level would have been: very slow or no recovery, because the conditions cannot reform autonomously below threshold. The actual recovery exceeds that prediction because intervention substituted for autonomous reformation.
        </p>
        <p className="finding-prose">
          <strong>Framework supplement:</strong> intervention can substitute for some structural conditions in the short term but requires continuous maintenance. The condor cohort is not yet self-sustaining — current population requires continued release-and-management, lead-toxicity treatment, and behavioral training. <strong>The conditions have not yet reformed autonomously.</strong> P7 tests this: ≥30% wild-wild reproduction by 2030 indicates autonomous reformation has begun.
        </p>

        <FindingSubhead>Boreal caribou — external-suppression dominance</FindingSubhead>
        <p className="finding-prose">
          Many boreal caribou herds maintain 4-5/7 structural conditions (cooperative behavior, site fidelity, cultural transmission of migration routes, kinship-network calf protection, dense aggregation in summer ranges) but populations decline 2-7% annually. The framework predicts stability or partial recovery; reality is decline.
        </p>
        <p className="finding-prose">
          The cause is external: climate change shifts vegetation patterns, alters predator dynamics (apparent competition with moose-supported wolf populations), and degrades calving habitat through industrial-development corridors. The structural conditions are intact; the suppression is on a different axis entirely.
        </p>
        <p className="finding-prose">
          <strong>Framework boundary:</strong> the seven conditions are necessary but not sufficient when external suppression dominates. Population recovery requires both structural conditions AND absence of dominant external suppression. The framework operates on the structural-conditions axis; it does not replace external-suppression analysis.
        </p>

        <FindingSubhead>Bald eagle — the counter-test</FindingSubhead>
        <p className="finding-prose">
          Bald eagle recovery (1963 → 300,000+ in 2024) was driven primarily by DDT ban — removal of an external suppression — not by structural-condition restoration. Bald eagle's structural conditions never decayed below ~4-5/7 (site fidelity, mate-finding intact, partial colony density). Once DDT eggshell-thinning was eliminated, the existing conditions sustained recovery.
        </p>
        <p className="finding-prose">
          <strong>The bald eagle case confirms that the framework predicts WHERE structural conditions are the operative variable, not where external suppression is.</strong> The framework's predictive power is highest when structural conditions are the operative variable; it asymptotes when external suppression dominates. Bald eagle's recovery exceeds what the seven-condition framework alone would predict — because the operative lever was external (DDT), not structural.
        </p>
      </FindingSection>

      {/* ── §6 Pre-registered predictions ── */}
      <FindingSection
        eyebrow="§6 Pre-registered predictions"
        title="Eight predictions. Resolution 2030-2035."
      >
        <p className="finding-prose">
          The cross-substrate framework makes eight testable predictions, resolving 2030-2035. P1-P5 test individual cases against the framework's predicted band. P6 (caribou) tests the external-suppression-dominance boundary. P7 (condor) tests the intervention-to-autonomy transition. P8 is the anti-prediction on marine teleost stock recovery — single-falsifier-suffices.
        </p>

        <PredictionsBlock
          title="Pre-registered predictions — sustained recovery, transitional, sustained collapse, framework-boundary tests"
          predictions={ECO_PREDICTIONS}
        />

        <p className="finding-prose">
          P6 and P7 are particularly informative: they actively test the framework's boundaries. If P6 falsifies (caribou recover with intact conditions despite climate change), the external-suppression-dominance claim is wounded. If P7 falsifies (condor remains intervention-dependent), the framework's "autonomous reformation requires above-threshold conditions" claim is supported as a hard constraint. Both directions of resolution are informative.
        </p>
      </FindingSection>

      {/* ── §7 Caveats — first-class content ── */}
      <FindingSection
        eyebrow="§7 Honest accounting"
        title="First-class caveats. Read before citing."
      >
        <FindingSubhead>Eco-substrate operationalization is approximate</FindingSubhead>
        <p className="finding-prose">
          The seven conditions translate from human to eco at varying confidence. <strong>High-confidence translations:</strong> E1 (aggregation density), E2 (habitat-network connectivity), E7 (site fidelity). <strong>Medium-confidence:</strong> E4 (cooperative breeding — only applies to species with allomothering; doesn't apply to broadcast spawners). <strong>Lower-confidence:</strong> E3 (status hierarchy varies enormously across taxa), E5 (cultural transmission only operates in social-learning species), E6 (mate-finding infrastructure varies from chemical to behavioral to physical). The cross-substrate framework is a coarse first approximation; species-specific mechanism dossiers are the resolution layer.
        </p>

        <FindingSubhead>Sample selection</FindingSubhead>
        <p className="finding-prose">
          Ten cases were chosen to cover the framework's prediction band cleanly (sustained recovery, transitional, sustained collapse) plus two boundary cases. A larger corpus (50+ IUCN red-list cases) would test selection bias but would dilute the cross-validation focus. v2 of this finding will integrate larger samples once predictions resolve.
        </p>

        <FindingSubhead>Allee dynamics vs structural conditions</FindingSubhead>
        <p className="finding-prose">
          The framework treats "below Allee threshold" as ~0-1/7 conditions, but Allee dynamics are themselves a specific subset of structural-condition dynamics (specifically, E1 aggregation density at sub-threshold values). The distinction matters: Allee theory is a well-developed body of population biology with its own predictions; the seven-condition framework partially encompasses Allee theory but extends to non-Allee cases (caribou, where conditions are intact but external suppression dominates). Cross-substrate readers should view the framework as <em>compatible with but distinct from</em> Allee theory.
        </p>

        <FindingSubhead>External-suppression vs structural-conditions</FindingSubhead>
        <p className="finding-prose">
          The boundary cases (caribou, bald eagle) document that the framework operates on the structural-conditions axis but does not replace external-suppression analysis. <strong>A fair reading of this finding requires both axes.</strong> Recovery requires structural conditions AND absence of dominant external suppression. Either alone is necessary-but-insufficient. The framework's predictive power is highest when structural conditions are the operative variable; it asymptotes when external suppression dominates.
        </p>

        <FindingSubhead>Intervention substitution depth</FindingSubhead>
        <p className="finding-prose">
          The condor case raises a deep question: if intensive intervention can substitute for some structural conditions in the short term, what's the long-term prediction? The framework's central claim is that conditions need to reform autonomously for sustained recovery. The condor case will resolve this — P7 tests whether wild-fledged-only reproduction reaches 30% by 2030. If yes, intervention substitution can produce reformed conditions; if no, intervention dependence remains and the framework's "autonomous reformation" claim holds.
        </p>

        <FindingSubhead>Reflexivity rating: MEDIUM</FindingSubhead>
        <p className="finding-prose">
          Wildlife population predictions can affect conservation funding and policy. A widely-cited finding that some cases (vaquita) are functionally extinct can produce defunding; a widely-cited finding that others (right whale) are predicted to decline can shape ship-strike regulations. The reflexivity is real but smaller than human-case findings (which can drive panic, sectarian backlash, or political instrumentalization). Framed as diagnostic + structural-condition map for conservation prioritization, not normative endorsement.
        </p>
      </FindingSection>

      {/* ── §8 Cross-references ── */}
      <FindingSection
        eyebrow="§8 What this means for the next question"
        title="The Continuum's substrate-symmetry, stress-tested."
      >
        <p className="finding-prose">
          This finding is the substrate-symmetric extension of the human-substrate corpus. The Continuum project's distinguishing thesis — that the same structural mechanism operates across substrates — now has a first cross-validation. The framework generalizes within boundaries; the boundaries (external-suppression, intervention-substitution) are themselves part of the framework's specification.
        </p>

        <FindingSubhead>Open questions for v7+</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>Backbone-vs-supporting test (Lab simulator).</strong> SC-PD-006 candidate card: vary which of the seven eco-conditions are present in synthetic eco-substrate populations (e.g., simulated cod-spawning aggregations) and observe whether population trajectory collapses faster when E1 (aggregation density) is removed vs E5 (cultural transmission). Tests the eco-substrate analog of the C1/C3/C5 backbone hypothesis from the human-case framework.</li>
          <li><strong>Threshold dynamics specification.</strong> Allee threshold values vary across taxa. A finer-grained finding could specify the threshold value (in % of pre-decline population) at which structural conditions cannot reform. Cross-case data supports a band (10-30% of pre-decline population) but the within-taxon precision is lower than the framework currently claims.</li>
          <li><strong>Intervention-to-autonomy transition.</strong> Condor (P7), whooping crane, and Iberian lynx all involve intervention-supported recovery. A future finding could specify the conditions under which intervention can be reduced without triggering re-collapse — when do the structural conditions become self-sustaining?</li>
          <li><strong>Cross-substrate predictions from human findings.</strong> The Korea-Hungary finding's "spending compensates at margins, cannot create structural conditions" claim has an eco analog: fishing moratoria compensate at margins (reduce mortality) but cannot recreate spawning aggregation density once collapsed. The substrate-symmetric reading of the Korea-Hungary finding is testable in real-time on multiple ongoing fishery moratoria.</li>
          <li><strong>Boundary-case framework supplements.</strong> Both caribou (external suppression) and condor (intervention substitution) document framework boundaries. A future finding could formally specify the conjunction conditions under which the framework's predictions hold vs need supplementation.</li>
        </ul>

        <FindingSubhead>Cross-references</FindingSubhead>
        <p className="finding-prose">
          This finding is the substrate-symmetric extension of <code>lab:finding/popdec/2026/refugia-template/v1</code> (the human-substrate cross-case framework). It is jointly read with the four prior popdec findings. Together the six findings form the complete corpus arc for the Continuum project's first phase.
        </p>
      </FindingSection>

      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>
        <dl className="finding-meta">
          <dt>Methodology Version</dt><dd>Lab V2.0</dd>
          <dt>Form</dt><dd>Form 4 — Pattern across cases (cross-substrate extension)</dd>
          <dt>Reflexivity</dt><dd>MEDIUM — wildlife population predictions can affect conservation funding and policy</dd>
          <dt>Epistemic status</dt><dd>Mechanism documented + cross-substrate validated</dd>
          <dt>Sample size</dt><dd>N=10 cases (3 sustained recovery + 2 transitional + 3 sustained collapse + 2 framework-boundary)</dd>
          <dt>Pre-registration date</dt><dd>2026-05-08. Resolution: 2030 (P1, P2, P3, P4, P5, P6, P7), 2035 (P8)</dd>
          <dt>Visibility</dt><dd>Public · read-only</dd>
          <dt>Citation handle</dt><dd><code>{FINDING_META.citationHandle}</code></dd>
          <dt>Falsifier</dt><dd>Any of: a documented case sustains population recovery with &lt;3 conditions; P1-P5 deviate beyond falsifier band; P6 (caribou framework-boundary test) falsifies; P7 (condor intervention-to-autonomy test) falsifies; P8 (anti-prediction) falsifies; a new sustained-recovery case has condition-count outside predicted band.</dd>
        </dl>
        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). Substrate-Symmetric Refugia: Whether the Seven-Condition Framework Predicts Population Recovery and Collapse Across Non-Human Cases. Lab Finding {FINDING_META.citationHandle}.
        </div>
        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-08 — initial pre-registration. Resolution windows: 2030, 2035.</p>
        </div>
      </footer>
    </article>
  );
}
